import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import {
    PolicyDialogComponent,
    TPolicyDialogData,
} from '@app/components/policy-dialog/policy-dialog.component';
import {
    TRouteAccessConfig,
    TPageType,
    TAllPageTypes,
    TChildPageType,
} from '@app/interfaces/route.interface';
import { UserModel } from '@app/models/user.model';
import { AppNavigationService } from '@app/services/app-navigation.service';
import { AppRouteService } from '@app/services/app-route.service';
import { AppService } from '@app/services/app.service';
import { UserService } from '@app/services/user.service';
import { TPolicyType } from '@app/types/policy.type';
import { translations } from '@app/utils/translations';
import { environment } from '@env/environment';
import { IMenuItem, KuiDialogService, KuiMenuComponent } from '@kone/ui';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'app-menu',
    imports: [KuiMenuComponent, TranslateModule],
    changeDetection: ChangeDetectionStrategy.Eager,
    template: `
        <kui-menu
            [items]="menu()"
            [isOpen]="menuState()"
            [closeOnOutsideClick]="false"
            (onItemClick)="handleMenuItemClick($event)"
            topHeaderOffset
            mode="persistent">
            <div kuiMenuFooter>
                <div class="flex flex-col text-xs">
                    <div class="flex flex-col justify-center items-center">
                        <button
                            type="button"
                            class="cursor-pointer hover:underline"
                            (click)="openPolicy('TERMS_AND_CONDITIONS')">
                            {{ translations.footer.terms_conditions | translate }}
                        </button>
                        <button
                            type="button"
                            class="text-primary cursor-pointer hover:underline"
                            (click)="openPolicy('DATA_PRIVACY')">
                            {{ translations.footer.data_privacy | translate }}
                        </button>
                    </div>
                    <div class="flex text-soft justify-center">
                        {{ translations.global.version | translate }} {{ environment.version }}
                    </div>
                </div>
            </div>
        </kui-menu>
    `,
})
export class MenuComponent {
    protected readonly translations = translations;
    protected readonly environment = environment;

    private appRouteService = inject(AppRouteService);
    private appNavigationService = inject(AppNavigationService);
    private kuiDialogService = inject(KuiDialogService);

    menuState = inject(AppService).menuState;
    user = inject(UserService).user;

    menu = computed(() => {
        const menu: IMenuItem[] = [];

        for (const route in this.appRouteService.routeAccess) {
            const pageType: TPageType = route as TPageType;
            if (!this.appRouteService.routeAccess[pageType].visibleOnMenu) continue;
            const item = this.getMenuItem(pageType, this.user());
            if (item) menu.push(item);
        }

        return menu;
    });

    getMenuItem(pageType: TPageType, user: UserModel): IMenuItem | undefined {
        const config = this.appRouteService.routeAccess[pageType];
        const routeAvailable: boolean = this.appRouteService.getRouteAvailability(config, user);
        const children: Partial<Record<TChildPageType, TRouteAccessConfig<TAllPageTypes>>> =
            config.routeConfig.children ?? {};

        if (!routeAvailable) return undefined;

        return {
            id: pageType,
            title: config.title ?? '',
            url: this.appRouteService.getPath(pageType),
            icon: config.icon,
            data: { page: pageType },
            children: Object.values(children)
                .filter(child => this.appRouteService.getRouteAvailability(child, user))
                .map(child => ({
                    id: child.routeConfig.path,
                    title: child.title ?? '',
                    url: this.appRouteService.getPath(pageType) + '/' + child.routeConfig.path,
                    icon: child.icon,
                    data: { page: pageType, child: child.routeConfig.path },
                })),
        };
    }

    handleMenuItemClick(event: { item: IMenuItem; parent?: IMenuItem }): void {
        const { item, parent } = event;

        if (parent) {
            this.appNavigationService.navigateTo(parent.data.page, {
                parameter: item.data.child,
                from: 'menu',
            });
        } else if (!item.children?.length) {
            this.appNavigationService.navigateTo(item.data.page, { from: 'menu' });
        }
    }

    openPolicy(type: TPolicyType): void {
        this.kuiDialogService.open<TPolicyDialogData>(
            PolicyDialogComponent,
            { width: '700px' },
            { mode: 'view', type },
        );
    }
}
